import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { meQuery, productReleasesQuery, productSearchQuery } from '../api/jedi'
import { LoadProblem } from '../shell/LoadProblem'
import type { ProductHit, ProductRelease, ProductSearchRequest } from '../api/types'

export const Route = createFileRoute('/_shell/servicenow-docs')({
  component: DocumentationGate,
})

/**
 * ServiceNow's documentation as JEDI serves it (ADR-0054): the releases the corpus service holds,
 * and a search over one. For the people who run the platform, to check what JEDI answers from.
 * URL-only, like /runs and /flags: no rail item mounts it.
 *
 * The gate here is cosmetic, as on /flags: the API refuses both routes without the Instruments
 * grant, and that is the boundary. A 503 from either means the documentation is unavailable, and
 * carries a sentence that LoadProblem shows as it is; it is never rendered as "nothing matches".
 */
function DocumentationGate() {
  const me = useQuery(meQuery)
  if (me.isPending) return null
  if (!me.data?.instruments) {
    return (
      <section className="surface">
        <header className="surface-head">
          <h1>ServiceNow documentation</h1>
          <p className="surface-sub">
            This page is for the people who run the platform. Ask one of them if you need
            something from it.
          </p>
        </header>
      </section>
    )
  }
  return <DocumentationPage />
}

const named = (release?: string | null) =>
  release ? release.charAt(0).toUpperCase() + release.slice(1) : ''

function DocumentationPage() {
  const releases = useQuery(productReleasesQuery)
  const [text, setText] = useState('')
  const [release, setRelease] = useState('')
  const [search, setSearch] = useState<ProductSearchRequest | null>(null)
  const results = useQuery({
    ...productSearchQuery(search ?? { query: '' }),
    enabled: search !== null,
  })
  const catalog = releases.data

  // The same question again asks again: an unchanged key would otherwise keep the last answer,
  // which after a failure means pressing Search did nothing.
  const submit = (event: FormEvent) => {
    event.preventDefault()
    const asked = { query: text.trim(), release: release || null }
    if (!asked.query) return
    if (search?.query === asked.query && search.release === asked.release) void results.refetch()
    else setSearch(asked)
  }

  return (
    <section className="surface surface-wide">
      <header className="surface-head">
        <div>
          <h1>ServiceNow documentation</h1>
          <p className="surface-sub">The releases J.E.D.I. answers from.</p>
        </div>
      </header>

      {releases.isPending && <p className="surface-note">Loading…</p>}
      <LoadProblem error={releases.error} />
      <div className="runs-list">
        {catalog?.releases?.map((line) => (
          <ReleaseRow key={line.release} line={line} isDefault={line.release === catalog.default} />
        ))}
      </div>

      <h2 className="surface-h2 runs-evals-head">Search</h2>
      <form className="admin-action" onSubmit={submit}>
        <label>
          Ask
          <input value={text} onChange={(e) => setText(e.target.value)} maxLength={1000} />
        </label>
        <label className="docs-release">
          Release
          <select value={release} onChange={(e) => setRelease(e.target.value)}>
            <option value="">{catalog ? `${named(catalog.default)} (default)` : 'Default'}</option>
            {catalog?.releases
              ?.filter((line) => line.release !== catalog.default)
              .map((line) => (
                <option key={line.release} value={line.release ?? ''}>
                  {named(line.release)}
                </option>
              ))}
          </select>
        </label>
        <button type="submit" disabled={!text.trim() || results.isFetching}>
          Search
        </button>
      </form>

      {results.isFetching && <p className="surface-note">Searching…</p>}
      <LoadProblem error={results.error} />
      {results.data && results.data.hits?.length === 0 && (
        <p className="surface-note">Nothing in {named(results.data.release)} matches.</p>
      )}
      <div className="docs-hits">
        {results.data?.hits?.map((hit) => <HitRow key={hit.path} hit={hit} />)}
      </div>
    </section>
  )
}

function ReleaseRow({ line, isDefault }: { line: ProductRelease; isDefault: boolean }) {
  const refused = line.state === 'refused'
  return (
    <div className="runs-row-block">
      <div className="runs-row">
        <span className={`plan-tag ${refused ? 'runs-fail' : 'plan-state'}`}>{line.state}</span>
        <span className="runs-intent">
          {named(line.release)}
          {isDefault ? ' · default' : ''}
        </span>
        <span className="runs-meta">
          {line.status} · {line.commit?.slice(0, 7)} · {Number(line.documents ?? 0).toLocaleString()}{' '}
          documents
        </span>
      </div>
      {refused && <p className="docs-refusal">{line.refusal}</p>}
    </div>
  )
}

function HitRow({ hit }: { hit: ProductHit }) {
  return (
    <article className="docs-hit">
      {hit.canonicalUrl ? (
        <a className="docs-hit-title" href={hit.canonicalUrl} target="_blank" rel="noreferrer">
          {hit.title}
        </a>
      ) : (
        <span className="docs-hit-title">{hit.title}</span>
      )}
      <p className="runs-meta">
        {hit.publication} · {named(hit.release)}
      </p>
      <p className="docs-hit-excerpt">{hit.excerpt}</p>
    </article>
  )
}
